import { textToBlockLetters, type Block, type Blocks } from '@w/content-blocks/src/block'
import { tableGrid, tableToHtml } from '@w/content-blocks/src/table-html'
import { validateLink } from '@w/md/src/parser/utils/link'
import { children, isAtom } from './tree-read'
import { mdToBlocks } from '@w/content-blocks/src/parser'
import { blocksToMd } from '@w/content-blocks/src/serializer'
import { defaultTreeAdapter, parseFragment, type DefaultTreeAdapterMap } from 'parse5'

/** Copied Blocks as HTML for other apps: standard blocks as their tags, any other block as its children's HTML. */
export function blocksToHtml(blocks: Blocks): string {
  const escape = (text: string) => text.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;')
  const wrappers: Partial<Record<Block['type'], string>> = { p: 'p', quote: 'blockquote', li: 'li' }
  return blocks.map((block): string => {
    const inner = blocksToHtml(children(block))
    if (block.type === 'text') return escape(block.text)
    if (block.type === 'chars') {
      let text = escape(block.text.map((part) => part.text).join(''))
      for (const [flag, tag] of [[16, 'code'], [8, 'u'], [4, 's'], [2, 'em'], [1, 'strong']] as const) {
        if ((block.format ?? 0) & flag) text = `<${tag}>${text}</${tag}>`
      }
      return text
    }
    if (block.type === 'br' || block.type === 'hr') return `<${block.type}>`
    if (block.type === 'h') return `<h${block.level}>${inner}</h${block.level}>`
    if (block.type === 'a') return `<a href="${escape(block.href)}">${inner}</a>`
    if (block.type === 'list') return block.subtype === 'ordered' ? `<ol>${inner}</ol>` : `<ul>${inner}</ul>`
    if (block.type === 'pre') return `<pre><code>${inner}</code></pre>`
    if (block.type === 'img') return `<img src="${escape(block.src)}" alt="${escape(block.alt ?? '')}">`
    if (block.type === 'table') return tableToHtml(block, blocksToHtml)
    const tag = wrappers[block.type]
    return tag ? `<${tag}>${inner}</${tag}>` : inner
  }).join('')
}

/** Clipboard HTML subset, converted through the shared Markdown codec without DOM globals. */
export function htmlToBlocks(html: string): Blocks { return importHTML(html) }

/** A parsed HTML element: tag, attributes and decoded text, as the WHATWG parser (parse5) builds them. */
interface HtmlElement { tag: string; attrs: Record<string, string>; children: (HtmlElement | string)[] }

const style = (node: HtmlElement) => node.attrs.style ?? ''
const hasLanguage = (node: HtmlElement) => 'data-language' in node.attrs || 'data-highlight-language' in node.attrs

/** Pasted HTML through the WHATWG tokenizer and tree builder (parse5 `parseFragment`): quoting, entities (named,
 * semicolonless, out of range) and misnesting resolve as in a browser's DOMParser. */
function parseHtml(html: string): HtmlElement {
  const convert = (node: DefaultTreeAdapterMap['childNode']): HtmlElement | string | undefined => {
    if (defaultTreeAdapter.isTextNode(node)) return defaultTreeAdapter.getTextNodeContent(node)
    if (!defaultTreeAdapter.isElementNode(node)) return undefined
    const nodes = 'content' in node ? node.content.childNodes : node.childNodes
    return { tag: node.tagName, attrs: Object.fromEntries(node.attrs.map(({ name, value }) => [name, value])),
      children: nodes.map((child) => convert(child)).filter((child) => child !== undefined) }
  }
  return { tag: '', attrs: {}, children: parseFragment(html).childNodes.map((child) => convert(child)).filter((child) => child !== undefined) }
}

const textOfHtml = (node: HtmlElement | string): string => typeof node === 'string' ? node : node.children.map(textOfHtml).join('')

/** Word list paragraphs (`mso-list`, their label in an `mso-list:Ignore` span) become lists numbered from 1 by their
 * items (BlockNote normalizeOfficeHTML). Each item keeps its own label and `levelN`: a number makes a numbered item, a
 * bullet glyph a bullet item, a deeper level nests under the item before it; any other label (iv., a.) ends the list
 * and stays the paragraph's text. */
function officeLists(node: HtmlElement): void {
  for (const child of node.children) if (typeof child !== 'string') officeLists(child)
  const items = node.children
  const marker = (item: HtmlElement) => item.children.find((child): child is HtmlElement =>
    typeof child !== 'string' && /mso-list\s*:\s*ignore/iu.test(style(child)))
  const kind = (item: HtmlElement | string | undefined): 'ol' | 'ul' | undefined => {
    if (typeof item === 'string' || item?.tag !== 'p' || !/mso-list\s*:\s*l\d/iu.test(style(item))) return undefined
    const label = textOfHtml(marker(item) ?? '').replaceAll('\u00A0', ' ').trim()
    return /^\d+[.)]$/u.test(label) ? 'ol' : /^[·•o§\-*\u00B7\uF0B7]$/u.test(label) ? 'ul' : undefined
  }
  for (let index = 0; index < items.length; index += 1) {
    if (!kind(items[index])) continue
    let root: HtmlElement | undefined
    const open: { level: number; list: HtmlElement; item?: HtmlElement }[] = []
    let end = index
    for (; kind(items[end]); end += 1) {
      const item = items[end] as HtmlElement
      const tag = kind(item) ?? 'ul'
      const level = Number(/level(\d+)/iu.exec(style(item))?.[1] ?? 1)
      while (open.length > 1 && (open.at(-1)?.level ?? 0) > level) open.pop()
      let top = open.at(-1)
      // A different kind at the same level starts a sibling list (inside the parent item, or a new top-level list).
      if (top && top.level >= level && top.list.tag !== tag) {
        if (open.length === 1) break
        open.pop()
        top = open.at(-1)
      }
      if (!top || top.level < level) {
        const list: HtmlElement = { tag, attrs: {}, children: [] }
        if (top?.item) top.item.children.push(list)
        else root ??= list
        open.push({ level, list })
        top = open.at(-1)
      }
      if (!top) break
      top.item = { tag: 'li', attrs: {}, children: item.children.filter((child) => child !== marker(item)) }
      top.list.children.push(top.item)
    }
    if (!root) continue
    items.splice(index, end - index, root)
  }
}

/** Consecutive monospace `white-space: pre` line divs (VS Code, Safari) are one code block, a blank line between lines
 * (Lexical PR #9460). */
function monospaceCode(node: HtmlElement): void {
  for (const child of node.children) if (typeof child !== 'string') monospaceCode(child)
  const items = node.children
  const isLine = (item: HtmlElement | string | undefined): item is HtmlElement => typeof item !== 'string' &&
    item?.tag === 'div' && /font-family\s*:[^;]*monospace/iu.test(style(item)) && /white-space\s*:\s*pre/iu.test(style(item))
  for (let index = 0; index < items.length; index += 1) {
    if (!isLine(items[index])) continue
    let end = index
    while (isLine(items[end + 1])) end += 1
    if (end === index) continue
    const lines = items.slice(index, end + 1).map((item) => textOfHtml(item))
    items.splice(index, end - index + 1, { tag: 'pre', attrs: {}, children: [lines.join('\n\n')] })
  }
}

/** An inline atom chip's payload (`{block}`) carries its typed identity as canonical Markdown. */
function atomMarkdown(payload: string): string | undefined {
  if (!payload) return undefined
  const parsed: unknown = JSON.parse(payload)
  const block = parsed && typeof parsed === 'object' && 'block' in parsed ? parsed.block as Block : undefined
  return block && isAtom(block) ? blocksToMd([{ id: '', type: 'p', children: [block] }]) : undefined
}

/** Text with no Markdown meaning: letters, digits, single-line punctuation that opens no construct, never a leading
 * `1.` list marker. */
const INERT_TEXT = /^(?!\d+\.)[\p{L}\p{N}][\p{L}\p{N} .,;?'"]*$/u

/** A `<p>` of plain text only, read as one text run without the Markdown round trip, as prosemirror-model's DOMParser
 * reads a paragraph by its schema rule (render's newline removal and trim, then mdToBlocks, give the same Block). */
function plainParagraph(node: HtmlElement): string | undefined {
  if (node.tag !== 'p' || 'style' in node.attrs || 'data-inline-text-payload' in node.attrs) return undefined
  let text = ''
  for (const child of node.children) {
    if (typeof child !== 'string') return undefined
    text += child
  }
  text = text.replaceAll(/\r?\n/gu, '').trim()
  return INERT_TEXT.test(text) ? text : undefined
}

function importHTML(html: string): Blocks {
  type Element = HtmlElement
  const root = parseHtml(html)
  const escape = (text: string) => text.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;')
  const openTag = (element: Element) => `<${element.tag}${Object.entries(element.attrs).map(([name, value]) => ` ${name}="${escape(value)}"`).join('')}>`
  // `bold`: an ancestor already bolds this text, so a nested <b>/<strong> adds nothing (Notion, rule
  // nux/transfer/paste-html-inline-styles imp30; ProseMirror parses nested marks once).
  const render = (node: Element | string, pre = false, bold = false): string => {
    if (typeof node === 'string') return pre ? node : node.replaceAll(/\r?\n/gu, '')
    const bolds = ['b', 'strong'].includes(node.tag) && !/font-weight\s*:\s*(normal|400)/iu.test(style(node))
    const text = node.children.map((child) => render(child, pre || node.tag === 'pre' || node.tag === 'code' && hasLanguage(node),
      bold || bolds)).join('')
    const attr = (name: string) => node.attrs[name] ?? ''
    if (['script', 'style', 'meta'].includes(node.tag)) return ''
    const atom = atomMarkdown(attr('data-inline-text-payload'))
    if (atom !== undefined) return atom
    if (node.tag === 'img') {
      const src = attr('src').replaceAll(/[\s()]/gu, (character) => encodeURIComponent(character).replace('(', '%28').replace(')', '%29'))
      return src ? `![${attr('alt').replaceAll(/[[\]\\]/gu, '\\$&')}](${src})` : ''
    }
    if (node.tag === 'br') return pre ? '\n' : '  \n'
    if (node.tag === 'hr') return '\n\n---\n\n'
    // A link the markdown parser refuses (`javascript:`, `data:`…) pastes as its text, never as a live or literal link
    // (BlockNote Link isAllowedUri).
    if (node.tag === 'a') {
      // WHATWG URL parsing drops tabs and newlines anywhere and C0 controls or spaces at the ends, so validate what a
      // browser would follow (`jav&#x09;ascript:` is `javascript:`).
      const raw = attr('href').replaceAll(/[\t\n\r]/gu, '')
      let from = 0
      let to = raw.length
      while (from < to && (raw.codePointAt(from) ?? 0) <= 0x20) from += 1
      while (to > from && (raw.codePointAt(to - 1) ?? 0) <= 0x20) to -= 1
      const href = raw.slice(from, to)
      return validateLink(href) ? `[${text}](${href})` : text
    }
    if (['b', 'strong'].includes(node.tag)) return bolds && !bold ? `**${text}**` : text
    if (['i', 'em'].includes(node.tag)) return `*${text}*`
    if (['s', 'strike', 'del'].includes(node.tag)) return `~~${text}~~`
    if (node.tag === 'u') return `::u[${text}]`
    if (node.tag === 'code' && 'data-inline-presentation-type' in node.attrs) return '$' + text + '$'
    if (node.tag === 'code') return pre ? text : attr('data-language') || attr('data-highlight-language')
      ? '\n\n```' + (attr('data-language') || attr('data-highlight-language')) + '\n' + text + '\n```\n\n' : '`' + text + '`'
    if (node.tag === 'pre') return '\n\n```' + attr('data-highlight-language') + '\n' + text + '\n```\n\n'
    if (/^h[1-6]$/u.test(node.tag)) return '\n\n' + '#'.repeat(Math.min(3,
      Number(node.tag[1]))) + ' ' + text.trim() + '\n\n'
    let styled = text
    if (/font-weight\s*:\s*(bold|[7-9]00)/iu.test(style(node))) styled = `**${styled}**`
    if (/font-style\s*:\s*italic/iu.test(style(node))) styled = `*${styled}*`
    if (/text-decoration[^:]*\s*:[^;]*underline/iu.test(style(node))) styled = `::u[${styled}]`
    if (node.tag === 'span') return styled
    if (node.tag === 'p' || node.tag === 'div' || node.tag === 'section') return '\n\n' + (pre ? styled.replaceAll(/^ +| +$/gu, (spaces) => '&#32;'.repeat(spaces.length)) : styled.trim()) + '\n\n'
    if (node.tag === 'li' && 'aria-checked' in node.attrs) return `[${attr('aria-checked') === 'true' ? 'x' : ' '}] ${text}`
    if (node.tag === 'li' && node.children.some((child) => typeof child !== 'string' && ['ul', 'ol'].includes(child.tag))) {
      return node.children.map((child) => render(child).trim()).filter(Boolean).join('\n')
    }
    if (node.tag === 'li' && !node.children.some((child) => typeof child !== 'string' && ['ul', 'ol'].includes(child.tag))) return text.trim().replaceAll(/\s*\n\s*/gu, '  \n')
    if (node.tag === 'ul' || node.tag === 'ol') return '\n\n' + node.children
      .filter((child): child is Element => typeof child !== 'string' && child.tag === 'li')
      .map((child, index) => (child.children.every((item) => typeof item === 'string' ? !item.trim() : ['ul', 'ol'].includes(item.tag))
        ? '  ' : node.tag === 'ol' ? `${index + 1}. ` : '- ') +
        render(child).trim().replaceAll('\n', node.tag === 'ol' ? '\n   ' : '\n  ')).join('\n') + '\n\n'
    if (node.tag === 'table') {
      const serialize = (element: Element | string, parent = ''): string => {
        if (typeof element === 'string') return element.trim() ? escape(/[\r\n]/u.test(element) || ['table', 'tbody', 'tr', 'td', 'th'].includes(parent)
          ? element.trim() : element) : ''
        if (element.tag === 'p' || element.tag === 'div') return '<br>' + element.children.map((child) => serialize(child, element.tag)).join('') + '<br>'
        if (element.tag === 'td' || element.tag === 'th') return openTag(element) +
          element.children.map((child) => serialize(child, element.tag)).join('').replaceAll(/(?:<br>){2,}/gu, '<br>').replaceAll(/^(?:<br>\s*)+|(?:<br>\s*)+$/gu, '') + `</${element.tag}>`
        const open = openTag(element)
        return open + element.children.map((child) => serialize(child, element.tag)).join('') +
          (['br', 'hr', 'img', 'col', 'input', 'wbr'].includes(element.tag) ? '' : `</${element.tag}>`)
      }
      return '\n\n' + serialize(node) + '\n\n'
    }
    return text
  }
  const blockTags = new Set(['p', 'div', 'section', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'pre', 'code', 'ul', 'ol', 'table', 'hr', 'br'])
  const parse = (items: (Element | string)[], pre = false): Blocks => {
    const blocks: Blocks = []
    let inline = ''
    const flush = () => {
      if (inline.trim()) blocks.push(...mdToBlocks(inline.trim(), { withChars: true }))
      inline = ''
    }
    for (const node of items) {
      if (typeof node === 'string' || !blockTags.has(node.tag) ||
        node.tag === 'code' && !hasLanguage(node)) {
        // A wrapper holding blocks at any depth (Word's `<html><body>`) is parsed through, not flattened to Markdown.
        const holdsBlocks = (item: Element): boolean => item.children.some((child) => typeof child !== 'string' &&
          (blockTags.has(child.tag) || holdsBlocks(child)))
        if (typeof node !== 'string' && holdsBlocks(node)) {
          flush()
          blocks.push(...parse(node.children, pre || /white-space\s*:\s*pre/iu.test(style(node))))
        } else if (typeof node === 'string' && blocks.length > 0) {
          // Bare text beside blocks is itself a block; a following inline element starts the next one.
          flush()
          inline = render(node)
          flush()
        } else inline += render(node)
        continue
      }
      flush()
      const plainText = pre ? undefined : plainParagraph(node)
      if (plainText !== undefined) blocks.push({ id: '', type: 'p', children: [{ id: '', type: 'chars', text: textToBlockLetters(plainText) }] })
      else if (node.tag === 'div' && node.children.some((child) => typeof child !== 'string' && blockTags.has(child.tag))) {
        blocks.push(...parse(node.children, pre || /white-space\s*:\s*pre/iu.test(style(node))))
      } else {
        const content = mdToBlocks(render(node, pre).trim(), { withChars: true })
        blocks.push(...(content.length > 0 ? content : (node.tag === 'p' || node.tag === 'br' || node.tag === 'section')
          ? [{ id: '', type: 'p' as const, children: [] }] : []))
      }
    }
    flush()
    return blocks
  }
  officeLists(root)
  monospaceCode(root)
  const blocks = parse(root.children)
  const completeTables = (items: Blocks) => {
    for (const block of items) {
      if (block.type === 'table') {
        delete block._colWidths
        const grid = tableGrid(block)
        const width = Math.max(1, ...grid.map((row) => row.length))
        for (const [y, row] of block.children.entries()) {
          for (let x = 0; x < width; x++) if (!grid[y][x]) {
            children(row).push({ id: '', type: 'td', children: [] })
          }
        }
      }
      completeTables(children(block))
    }
  }
  completeTables(blocks)
  return blocks
}
